import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { MailrailAutoSendEmailCampaign } from '@feathr/blackbox';
import type { IRadioOption } from '@feathr/components';
import { Radios } from '@feathr/components';
import { useStore } from '@feathr/extender/state';

interface IProps {
  campaign: MailrailAutoSendEmailCampaign;
}

function MailrailBulkSendConfig({ campaign }: Readonly<IProps>): JSX.Element {
  const { Segments } = useStore();
  const { t } = useTranslation();
  const predicates = campaign.get('predicates');
  const mode = campaign.get('predicates_mode') ?? 'match_any';
  const selectedOption = campaign.get('bulk_send_enabled')
    ? campaign.get('bulk_send_timing')
    : 'none';
  const isTimeSubtype = campaign.get('subtype') === 'time';
  const includedSegmentIds = campaign.get('segments_included', []).filter(Boolean);
  const excludedSegmentIds = campaign.get('segments_excluded', []).filter(Boolean);
  const [previouslyMatchedCount, setPreviouslyMatchedCount] = useState(0);

  useEffect(() => {
    async function getUniqueEmails(): Promise<void> {
      // Build an ephemeral segment to filter the user's target audience for persons who already
      // satisfied the campaign's trigger predicates
      const triggerPredicateSegment = Segments.create({
        account: campaign.get('account'),
        is_conversion_segment: false,
        is_custom: false,
        lookback_mode: 'unbounded',
        mode: mode,
        predicates: predicates.map((p) => (p.kind === 'update' ? { ...p, kind: 'attribute' } : p)),
      });
      const uniqueEmailsRequest = {
        included_segment_ids: includedSegmentIds,
        excluded_segment_ids: excludedSegmentIds,
        ...(includedSegmentIds.length
          ? { filter_segment: triggerPredicateSegment.toJS() }
          : { included_segments: [triggerPredicateSegment.toJS()] }),
      };
      try {
        const response = await Segments.getUniqueEmailsFromSegments(uniqueEmailsRequest);
        if (response) {
          setPreviouslyMatchedCount(response.unique_emails);
        }
      } catch (error) {
        // Set the unique emails count to 0 instead of showing the error
        setPreviouslyMatchedCount(0);
      }
    }

    predicates.length ? getUniqueEmails() : setPreviouslyMatchedCount(0);
  }, [includedSegmentIds.join(','), excludedSegmentIds.join(','), JSON.stringify(predicates)]);

  useEffect(() => {
    if (!previouslyMatchedCount && selectedOption === 'start') {
      campaign.set({ bulk_send_enabled: false });
    }
  }, [previouslyMatchedCount]);

  function handleBulkSendChange(newValue?: string): void {
    if (newValue === 'none') {
      campaign.set({ bulk_send_enabled: false });
    } else if (newValue === 'start' || newValue === 'end') {
      campaign.set({ bulk_send_enabled: true, bulk_send_timing: newValue });
    }
  }

  function getStartOptionTooltip(): string {
    if (!previouslyMatchedCount) {
      return t(
        'This option is unavailable because there are no contacts in your audience who have previously matched your selected triggering filters.',
      );
    }
    return t(
      'At the start of the campaign, {{ count, number }} contacts who have previously matched your selected triggering filters will be emailed immediately.',
      { count: previouslyMatchedCount },
    );
  }

  function getEndOptionTooltip(): string {
    if (!includedSegmentIds.length && campaign.get('repeat_allowed')) {
      return t(
        'This option is unavailable because it requires at least one included segment and cannot be combined with the "Send every time" cadence.',
      );
    }
    if (!includedSegmentIds.length) {
      return t('This option is unavailable because it requires at least one included segment.');
    }
    if (campaign.get('repeat_allowed')) {
      return t(
        'This option is unavailable because it cannot be combined with the "Send every time" cadence.',
      );
    }
    return t(
      'At the end of the campaign, the email will be sent to all the targeted contacts who have not yet received it.',
    );
  }

  const bulkSendOptions: IRadioOption[] = [
    {
      id: 'none',
      name: t('None'),
      tooltip: t(
        'No bulk send will be performed. Your target audience will only be emailed if and when they perform the trigger.',
      ),
    },
    {
      id: 'start',
      name: t('At start time'),
      disabled: isTimeSubtype || !previouslyMatchedCount,
      tooltip: getStartOptionTooltip(),
    },
    {
      id: 'end',
      name: t('At end time'),
      disabled: isTimeSubtype || !includedSegmentIds.length || campaign.get('repeat_allowed'),
      tooltip: getEndOptionTooltip(),
    },
  ];

  return (
    <Radios
      dataName={'bulk_send_options'}
      helpText={t('Choose whether to include a bulk send at the beginning or end of the campaign.')}
      label={t('Bulk send')}
      layout={'block'}
      onChange={handleBulkSendChange}
      options={bulkSendOptions}
      value={selectedOption}
    />
  );
}

export default observer(MailrailBulkSendConfig);
